import { fireMetaConversionEvent } from "./metaAds";

/**
 * Centralized GA4 / GTM analytics module.
 * All dataLayer.push calls go through here — never scatter them across components.
 *
 * GTM is initialized in src/index.js when REACT_APP_GTM_ID is set.
 */

const push = (event) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
};

// Module-level currency — updated by MarketContext when market changes.
// Keeps all analytics events in sync with the current market without
// requiring every call site to pass the currency explicitly.
let _currency = 'INR';
export const setAnalyticsCurrency = (currencyCode) => { _currency = currencyCode || 'INR'; };

// ── Feed ID — must exactly match g:id in GoogleFeedService.java ────────────────
//
// Feed format:  "{prefix}-{productId}"               single-variant products
//               "{prefix}-{productId}-{variantId}"   multi-variant products
//
// The prefix is configured per tenant in the backend (tenants.config.feed_id_prefix)
// and arrives with the Google Ads public config (see utils/googleAds.js).
let _feedIdPrefix = 'sktn';
export const setFeedIdPrefix = (prefix) => { if (prefix) _feedIdPrefix = prefix; };

const feedId = (product, variant) => {
  const productId = product.productId ?? product.id;
  const multiVariant = (product.variants?.length || 0) > 1;
  if (multiVariant && variant?.id) return `${_feedIdPrefix}-${productId}-${variant.id}`;
  return `${_feedIdPrefix}-${productId}`;
};

// ── Variant resolution ─────────────────────────────────────────────────────────
//
// Cart items store selectedVariant as a title string (e.g. "100ml").
// Attempts to find the matching variant object; falls back to variants[0].
const resolveVariant = (product) => {
  const variants = product.variants;
  if (!variants?.length) return null;
  if (product.selectedVariant) {
    const match = variants.find(
      (v) =>
        v.title === product.selectedVariant ||
        v.option1 === product.selectedVariant ||
        v.size === product.selectedVariant
    );
    if (match) return match;
  }
  return variants[0];
};

// ── Shared item builder ────────────────────────────────────────────────────────
//
// variant param: pass the currently-selected variant object when known (e.g. on
// the product detail page). Falls back to resolveVariant for cart items.
const toItem = (product, quantity = 1, index = 0, variant = null) => {
  const v = variant || resolveVariant(product);

  const price     = Number(v?.price      ?? product.price)      || 0;
  const compareAt = Number(v?.compareAtPrice ?? product.compareAtPrice) || 0;
  const discount  = compareAt > price ? Math.round((compareAt - price) * 100) / 100 : 0;

  // item_variant: skip generic placeholder titles
  const rawVariant =
    v?.title ||
    (typeof product.selectedVariant === 'string' ? product.selectedVariant : null);
  const item_variant =
    rawVariant && rawVariant !== 'Default' && rawVariant !== 'Default Title'
      ? rawVariant
      : undefined;

  return {
    item_id:       feedId(product, v),
    item_name:     product.title || product.name,
    item_brand:    product.vendor || product.brand,
    item_category: product.productType || product.type,
    ...(item_variant !== undefined ? { item_variant } : {}),
    price,
    discount,
    quantity,
    index,
  };
};

// ── Events ─────────────────────────────────────────────────────────────────────

// variant: the currently-selected ProductVariant object (optional but recommended)
export const trackViewItem = (product, variant = null) => {
  const fbv = variant || resolveVariant(product);
  fireMetaConversionEvent("ViewContent", {
    content_name: product.title,
    content_ids: [feedId(product, fbv)],
    content_type: "product",
    value: Number(fbv?.price ?? product.price) || 0,
    currency: "INR"
  });

  push({ ecommerce: null });
  const v     = variant || resolveVariant(product);
  const price = Number(v?.price ?? product.price) || 0;
  push({
    event: 'view_item',
    ecommerce: {
      currency: _currency,
      value:    price,
      items:    [toItem(product, 1, 0, variant)],
    },
  });
};

export const trackAddToCart = (product, quantity = 1) => {
  fireMetaConversionEvent("AddToCart", {
    content_name: product.title,
    content_ids: [feedId(product, resolveVariant(product))],
    content_type: "product",
    value: (Number(product.price) || 0) * quantity,
    currency: "INR"
  });

  push({ ecommerce: null });
  push({
    event: 'add_to_cart',
    ecommerce: {
      currency: _currency,
      value:    (Number(product.price) || 0) * quantity,
      items:    [toItem(product, quantity)],
    },
  });
};

export const trackRemoveFromCart = (product, quantity = 1) => {
  push({ ecommerce: null });
  push({
    event: 'remove_from_cart',
    ecommerce: {
      currency: _currency,
      value:    (Number(product.price) || 0) * quantity,
      items:    [toItem(product, quantity)],
    },
  });
};

export const trackViewCart = (items) => {
  push({ ecommerce: null });
  push({
    event: 'view_cart',
    ecommerce: {
      currency: _currency,
      value:    items.reduce((sum, i) => sum + (Number(i.price) || 0) * (i.quantity || 1), 0),
      items:    items.map((i, idx) => toItem(i, i.quantity || 1, idx)),
    },
  });
};

export const trackBeginCheckout = (items, coupon) => {
  push({ ecommerce: null });
  push({
    event: 'begin_checkout',
    ecommerce: {
      currency: _currency,
      value:    items.reduce((sum, i) => sum + (Number(i.price) || 0) * (i.quantity || 1), 0),
      coupon:   coupon || undefined,
      items:    items.map((i, idx) => toItem(i, i.quantity || 1, idx)),
    },
  });
};

export const trackPurchase = (order) => {
  fireMetaConversionEvent("Purchase", {
    value: Number(order.total || order.totalAmount) || 0,
    currency: "INR",
    content_ids: (order.items || []).map(i => feedId(i, resolveVariant(i))),
    content_type: "product"
  }, order.orderNumber || order.orderId || order.id);

  push({ ecommerce: null });
  push({
    event: 'purchase',
    ecommerce: {
      transaction_id: order.orderNumber || order.orderId || order.id,
      currency:       'INR',
      value:          Number(order.total || order.totalAmount) || 0,
      tax:            0,
      shipping:       Number(order.shippingAmount) || 0,
      coupon:         order.couponCode || undefined,
      items:          (order.items || []).map((i, idx) => toItem(i, i.quantity || 1, idx)),
    },
  });
};

// ── Dynamic remarketing ────────────────────────────────────────────────────────

export const pushRemarketing = (data) => {
  push({
    event:             'remarketing',
    google_tag_params: data,
  });
};

// variant: the currently-selected ProductVariant object (optional but recommended)
export const remarketingProductPage = (product, variant = null) => {
  const v = variant || resolveVariant(product);
  pushRemarketing({
    ecomm_prodid:     feedId(product, v),
    ecomm_pagetype:   'product',
    ecomm_totalvalue: Number(v?.price ?? product.price) || 0,
  });
};

export const remarketingCartPage = (items) =>
  pushRemarketing({
    ecomm_prodid:     items.map((i) => feedId(i, resolveVariant(i))),
    ecomm_pagetype:   'cart',
    ecomm_totalvalue: items.reduce((s, i) => s + (Number(i.price) || 0) * (i.quantity || 1), 0),
  });

export const remarketingPurchasePage = (order) =>
  pushRemarketing({
    ecomm_prodid:     (order.items || []).map((i) => feedId(i, resolveVariant(i))),
    ecomm_pagetype:   'purchase',
    ecomm_totalvalue: Number(order.total || order.totalAmount) || 0,
  });
